import { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from "react";
import styles from "./OfferPreview.module.scss";
import { LoadingOutlined } from "@ant-design/icons";
import { message, Spin } from "antd";
import { Modal } from "../modal";
import {
  getOfferConfirmAgreement,
  getOfferHtml
} from "~/pages/questionnaires/v2/customer/api/apiMethods";
import { OfferContent } from "./OfferContent";
import { OfferResult } from "./OfferResult";
import { AttachmentsDetailsError } from "~/typings/types";

const antIcon = <LoadingOutlined className={styles.spin} spin />;

interface OfferPreviewProps {
  clientId: string;
  isShow: boolean;
  toggle: () => void;
  setErrorUploadOffer: Dispatch<AttachmentsDetailsError>;
  setIsGeneratedOffer: Dispatch<SetStateAction<boolean>>;
}

export const OfferPreview: FC<OfferPreviewProps> = ({
  clientId,
  isShow,
  toggle,
  setErrorUploadOffer,
  setIsGeneratedOffer
}) => {
  const [offerHtml, setOfferHtml] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2 | null>(null);
  const [consentChecked, setConsentChecked] = useState<boolean>(false);
  const [creationDate, setCreationDate] = useState<Date | null>(null);
  const [isLoadingGetOffer, setIsLoadingGetOffer] = useState<boolean>(false);
  const [isLoadingConfirmAgreement, setIsLoadingConfirmAgreement] = useState<boolean>(false);

  const getContent = useCallback(async (): Promise<void> => {
    try {
      setIsLoadingGetOffer(true);
      const response = await getOfferHtml(clientId);

      if (response?.data && typeof response.data === "string") {
        setOfferHtml(response.data);
        setErrorUploadOffer(null);
      } else {
        message.error("Не удалось загрузить документ");
      }
    } catch {
      message.error("Ошибка загрузки документа");
    } finally {
      setIsLoadingGetOffer(false);
    }
  }, [clientId, setErrorUploadOffer]);

  useEffect(() => {
    if (isShow) {
      getContent();
    }
  }, [isShow, getContent]);

  const handleAgree = async () => {
    try {
      setIsLoadingConfirmAgreement(true);
      await getOfferConfirmAgreement(clientId);
      setCreationDate(new Date());
      setStep(2);
      setIsGeneratedOffer(true);
    } catch {
      message.error("Ошибка подтверждения соглашения");
    } finally {
      setIsLoadingConfirmAgreement(false);
    }
  };

  const handleEdit = () => {
    setStep(2);
  };

  const handleToggleModal = () => {
    toggle();
    setStep(null);
    setConsentChecked(false);
  };

  return (
    <Modal isShow={isShow} toggle={handleToggleModal} isShowCloseButton={!isLoadingGetOffer}>
      <div className={styles.container}>
        {isLoadingGetOffer ? (
          <Spin indicator={antIcon} className={styles.containerSpin} />
        ) : !offerHtml ? (
          <p className={styles.documentNotFound}>Документ не найден</p>
        ) : step === null ? (
          <OfferContent
            offerHtml={offerHtml}
            consentChecked={consentChecked}
            setConsentChecked={setConsentChecked}
            isLoadingConfirmAgreement={isLoadingConfirmAgreement}
            handleAgree={handleAgree}
            handleEdit={handleEdit}
          />
        ) : (
          <OfferResult
            creationDate={creationDate}
            toggle={handleToggleModal}
            isLoadingConfirmAgreement={isLoadingConfirmAgreement}
            clientId={clientId}
          />
        )}
      </div>
    </Modal>
  );
};
